import { useEffect, useState } from 'react'
import {
  useSystemHealthConfig,
  useUpdateSystemHealthConfig,
  useSystemHealthRecipients,
  useSendSystemHealthReport,
} from '../../../hooks/admin/useSystemHealth'
import type { SystemHealthConfig } from '../../../types/systemHealth'

type FormState = Omit<SystemHealthConfig, 'id'>

export function AlertSettingsSection() {
  const { data: config, isLoading } = useSystemHealthConfig()
  const { data: recipients } = useSystemHealthRecipients()
  const updateConfig = useUpdateSystemHealthConfig()
  const sendReport = useSendSystemHealthReport()
  const [form, setForm] = useState<FormState | null>(null)

  useEffect(() => {
    if (config) {
      const { id, ...rest } = config
      setForm(rest)
    }
  }, [config])

  const enabledRecipients = (recipients ?? []).filter((r) => r.enabled).length

  if (isLoading || !form) {
    return (
      <section className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <p className="text-gray-600">Loading alert settings...</p>
      </section>
    )
  }

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev))

  return (
    <section className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Pipeline Alerts</h2>
        <p className="text-gray-600 mt-1 text-sm">
          Pages the ops recipient list when the message pipeline backs up or starts failing, in a sustained way
          (not transient queue lag).
        </p>
      </div>

      {/* Master switch */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => set('enabled', e.target.checked)}
            className="rounded border-gray-300 h-5 w-5"
          />
          <span className="text-lg font-semibold">Enable pipeline monitoring</span>
        </label>
        <p className="text-gray-500 text-sm mt-2">
          Requires the <code>system_health_alerts</code> feature flag to also be on in the processor.
        </p>
        <label className="flex items-center gap-3 cursor-pointer mt-4">
          <input
            type="checkbox"
            checked={form.recoveryEnabled}
            onChange={(e) => set('recoveryEnabled', e.target.checked)}
            className="rounded border-gray-300 h-4 w-4"
          />
          <span>Send recovery (“all clear”) notifications</span>
        </label>
        <label className="flex items-center gap-3 cursor-pointer mt-4">
          <input
            type="checkbox"
            checked={form.failedAlertsEnabled}
            onChange={(e) => set('failedAlertsEnabled', e.target.checked)}
            className="rounded border-gray-300 h-4 w-4"
          />
          <span>Alert on permanently failed messages</span>
        </label>
        <p className="text-gray-500 text-sm mt-2">
          Messages that ended in a terminal error are never retried. Without this they drop out of the
          backlog silently.
        </p>
      </div>

      {/* Thresholds */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
        <h3 className="text-lg font-semibold">Thresholds</h3>
        <div className="grid grid-cols-2 gap-4">
          <NumberField
            label="Backlog count threshold"
            hint="Unprocessed messages before a backlog alert"
            value={form.backlogCountThreshold}
            onChange={(v) => set('backlogCountThreshold', v)}
          />
          <NumberField
            label="Backlog age (minutes)"
            hint="Oldest unprocessed message age that trips an alert"
            value={form.backlogAgeMinutes}
            onChange={(v) => set('backlogAgeMinutes', v)}
          />
          <NumberField
            label="Sustained checks"
            hint="Consecutive 30s checks a backlog must persist before firing"
            value={form.sustainedChecks}
            onChange={(v) => set('sustainedChecks', v)}
          />
          <NumberField
            label="Poison trend checks"
            hint="Checks the poison queue must keep growing before firing"
            value={form.poisonTrendChecks}
            onChange={(v) => set('poisonTrendChecks', v)}
          />
          <NumberField
            label="Stalled count floor"
            hint="Backlog size considered “large” for stall detection"
            value={form.stalledCountThreshold}
            onChange={(v) => set('stalledCountThreshold', v)}
          />
          <NumberField
            label="Stalled checks"
            hint="Checks of non-stop growth (no draining) before a stall alert"
            value={form.stalledChecks}
            onChange={(v) => set('stalledChecks', v)}
          />
          <NumberField
            label="Retry budget"
            hint="Send attempts before giving up (on send failure)"
            value={form.retryBudget}
            onChange={(v) => set('retryBudget', v)}
            max={10}
          />
          <NumberField
            label="Failed-message threshold"
            hint="Permanently failed messages in the window before alerting"
            value={form.failedCountThreshold}
            onChange={(v) => set('failedCountThreshold', v)}
            max={100000}
          />
          <NumberField
            label="Failed lookback (minutes)"
            hint="How far back to count failures (1440 = 24 hours)"
            value={form.failedLookbackMinutes}
            onChange={(v) => set('failedLookbackMinutes', v)}
            max={10080}
          />
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => updateConfig.mutate(form)}
            disabled={updateConfig.isPending}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {updateConfig.isPending ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      {/* Send test report */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold">Send status report</h3>
        <p className="text-gray-500 text-sm mt-1">
          Sends a current pipeline-status snapshot to all enabled recipients through the real delivery pipeline, so
          you can preview exactly what an alert looks like. Arrives in ~30s.
        </p>
        <div className="flex items-center gap-4 mt-4">
          <button
            type="button"
            onClick={() => sendReport.mutate()}
            disabled={sendReport.isPending || enabledRecipients === 0}
            className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
          >
            {sendReport.isPending ? 'Sending...' : 'Send Test Report'}
          </button>
          <span className="text-sm text-gray-500">
            {enabledRecipients === 0
              ? 'Add at least one enabled recipient below.'
              : `Will send to ${enabledRecipients} enabled recipient${enabledRecipients === 1 ? '' : 's'}.`}
          </span>
        </div>
      </div>
    </section>
  )
}

/**
 * `max` should mirror the server-side yup bound in the API's
 * routes/admin/system-health/config.ts. Without it an out-of-range value is
 * only rejected on save, surfacing as a bare 400 toast with no indication of
 * which field was wrong.
 */
function NumberField({
  label,
  hint,
  value,
  onChange,
  max,
}: {
  label: string
  hint?: string
  value: number
  onChange: (v: number) => void
  max?: number
}) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <input
        type="number"
        value={value}
        step={1}
        min={1}
        max={max}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
      />
      {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
    </div>
  )
}
